import React, { useState } from 'react';
import { useBMP } from '../context/BMPContext';
import useMarketBids from '../hooks/useMarketBids';
import BidFormModal from '../forms/BidFormModal';
import { useTranslation } from 'react-i18next';
import './QuotaDetailView.css';

function formatCurrency(val) {
  return parseFloat(val || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function QuotaDetailView() {
  const { t } = useTranslation();
  const { viewParams, isProducer, bmpNavigate } = useBMP();
  const { createBid, saving } = useMarketBids();
  const [showBidForm, setShowBidForm] = useState(false);
  const q = viewParams?.quota;

  if (!q) {
    return (
      <div className="bmp-empty">
        <p>{t('bmp_quota_not_found')}</p>
        <button className="bmp-btn bmp-btn-primary" onClick={() => bmpNavigate('catalog')}>{t('bmp_back_catalog')}</button>
      </div>
    );
  }

  const canBid = !isProducer && q.status !== 'sold' && q.status !== 'expired';

  return (
    <div className="quota-detail">
      <button className="bmp-btn-ghost bmp-back" onClick={() => bmpNavigate('catalog')}>
        &#8592; {t('bmp_back_catalog')}
      </button>

      <div className="quota-detail-grid">
        {/* Left — main info */}
        <div className="quota-detail-main">
          <div className="quota-detail-hero">
            <span className="quota-detail-hero-icon">&#127793;</span>
            <span className={`bmp-status bmp-status--${q.status}`}>{q.status_display}</span>
          </div>

          <h1 className="quota-detail-title">{q.farm_name}</h1>
          {!isProducer && (
            <p className="quota-detail-producer">
              {q.producer_company || q.producer_username}
            </p>
          )}

          <div className="quota-detail-badges">
            <span className={`bmp-tag bmp-zone--${q.farm_zone}`}>{q.farm_zone_display}</span>
            {q.farm_city && <span className="bmp-tag">{q.farm_city}</span>}
            <span className="bmp-tag">{q.farm_fruit_type === 'organico' ? t('mkt_organic') : q.farm_fruit_type === 'ambos' ? t('mkt_both_fruit') : t('mkt_conventional')}</span>
          </div>

          {q.notes && (
            <div className="quota-detail-notes">
              <strong>{t('mkt_notes')}:</strong> {q.notes}
            </div>
          )}

          <div className="quota-detail-meta">
            <span>{t('mkt_week_short')}: <strong>{q.week_start}</strong></span>
          </div>
        </div>

        {/* Right — action panel */}
        <div className="quota-detail-action bmp-card">
          <div className="quota-detail-stats">
            <div className="qd-stat">
              <span className="qd-stat-val">{q.remaining_boxes.toLocaleString('es-EC')}</span>
              <span className="qd-stat-label">{t('bmp_boxes_available')}</span>
            </div>
            <div className="qd-stat">
              <span className="qd-stat-val qd-stat--muted">{q.available_boxes.toLocaleString('es-EC')}</span>
              <span className="qd-stat-label">{t('bmp_total_offered')}</span>
            </div>
            {isProducer && q.min_price && (
              <div className="qd-stat">
                <span className="qd-stat-val">${formatCurrency(q.min_price)}</span>
                <span className="qd-stat-label">{t('mkt_min_price')}</span>
              </div>
            )}
          </div>

          {isProducer && (
            <div className="qd-bids-count">
              <strong>{q.bids_count}</strong> {t('bmp_pending_bids')}
            </div>
          )}

          {canBid && (
            <button className="bmp-btn bmp-btn-primary qd-bid-btn" onClick={() => setShowBidForm(true)}>
              {t('bmp_make_bid')}
            </button>
          )}

          {!canBid && !isProducer && (
            <p className="qd-closed">{t('bmp_quota_closed')}</p>
          )}
        </div>
      </div>

      {showBidForm && (
        <BidFormModal
          quota={q}
          onClose={() => setShowBidForm(false)}
          onSave={async (data) => {
            await createBid(data);
            setShowBidForm(false);
            bmpNavigate('my_bids');
          }}
          saving={saving}
        />
      )}
    </div>
  );
}
